import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/Badge";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckSquare,
  Clock,
  FileText,
  Inbox,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Save,
  X,
} from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  UnsavedChangesDialog,
  useUnsavedChangesPrompt,
} from "@/components/UnsavedChangesPrompt";
import { toast } from "@/hooks/use-toast";
import { Container } from "@/components/ui/Container";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import {
  AttachmentSection,
  EmailSourceSection,
} from "@/features/order-confirmation/components/OcSource";
import { ExtractionLink } from "@/features/documents/ExtractionLink";
import {
  UseSaved,
  type PresetOption,
} from "@/features/sales-order/components/UseSaved";
import { useCustomerDetail } from "@/features/sales-order/customerPresets";
import {
  SalesOrderHeadPanel,
  SalesOrderRowsPanel,
} from "@/features/sales-order/components/SalesOrderPanels";
import {
  DocumentNav,
  type OrderViewMode,
} from "@/features/sales-order/components/DocumentNav";
import {
  invalidateSalesOrderData,
  salesOrderDetailQueryKeys,
  salesOrderQueryOptions,
} from "@/features/sales-order/salesOrderQueries";
import {
  ApiRequestError,
  createCustomerOrderRow,
  deleteCustomerOrderRow,
  createCustomerOrderSalesOrder,
  getCustomerOrderDetail,
  getErpInventory,
  markManualHandlingCustomerOrder,
  saveCustomerOrderDraft,
  searchErpArticles,
  searchErpCustomers,
  skipCustomerOrder,
} from "@/services/api";
import { SALES_ORDER_METRIC_COPY } from "@/features/sales-order/salesOrderMetricCopy";
import { AddRowPopover } from "@/features/sales-order/components/AddRowPopover";
import { CustomerOrderAuditLog } from "@/features/sales-order/components/CustomerOrderAuditLog";
import { ErpTypeahead } from "@/features/sales-order/components/ErpTypeahead";
import {
  formatAmount,
  statedTotal,
  type StatedTotals,
} from "@/features/sales-order/amount";
import type {
  CustomerOrderDetail,
  CustomerOrderDraft,
  CustomerOrderHead,
  CustomerOrderRow,
  CustomerOrderRowDraft,
  CustomerAddress,
  CustomerContactPerson,
  NewCustomerOrderRow,
} from "@/types/salesOrder";

function formatNumber(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 2 }).format(
    value,
  );
}

/** Extracted dates arrive as plain strings, empty when the document did not state one. */
function formatDate(raw: string | undefined): string | null {
  return raw?.trim() ? raw.trim() : null;
}

/**
 * The delivery address as its own value, so it can be shown, compared and corrected as one.
 *
 * Every part of it the ERP is sent is here, additional_information among them: the customer's
 * stored addresses carry things like which gate to deliver to, and a part of the address nobody
 * can see or correct is worse than one that is not sent at all.
 */
interface DeliveryAddress {
  company_name: string;
  care_of: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country: string;
  additional_information: string;
}

const EMPTY_ADDRESS: DeliveryAddress = {
  company_name: "",
  care_of: "",
  street: "",
  house_number: "",
  postal_code: "",
  city: "",
  country: "",
  additional_information: "",
};

function addressFromHead(
  head: CustomerOrderHead | null | undefined,
): DeliveryAddress {
  if (!head) return EMPTY_ADDRESS;
  return {
    company_name: head.delivery_address_company_name,
    care_of: head.delivery_address_care_of,
    street: head.delivery_address_street,
    house_number: head.delivery_address_house_number,
    postal_code: head.delivery_address_postal_code,
    city: head.delivery_address_city,
    country: head.delivery_address_country,
    additional_information: head.delivery_address_additional_information,
  };
}

/** Written the way an address is written, not the way it is stored. */
function addressLines(address: DeliveryAddress): string[] {
  const street = [address.street, address.house_number]
    .filter(Boolean)
    .join(" ");
  const city = [address.postal_code, address.city].filter(Boolean).join(" ");
  // Written the way it is written on an envelope, so it reads as an instruction about the
  // address rather than as another name in it.
  const careOf = address.care_of.trim() ? `c/o ${address.care_of.trim()}` : "";
  return [
    address.company_name,
    careOf,
    street,
    city,
    address.country,
    address.additional_information,
  ].filter((line) => Boolean(line?.trim()));
}

function AddressLines({ address }: { address: DeliveryAddress }) {
  const lines = addressLines(address);
  if (lines.length === 0)
    return <p className="text-sm text-gray-400">No delivery address</p>;
  return (
    <address className="block text-sm not-italic leading-snug text-gray-700">
      {lines.map((line, i) => (
        <span key={i} className="block">
          {line}
        </span>
      ))}
    </address>
  );
}

function AddressField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="h-6 w-full min-w-0 rounded border border-input bg-white px-1.5 text-sm text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15"
    />
  );
}

/**
 * Our delivery address: read as an address, corrected a part at a time.
 *
 * It presents as the block on the other side of the comparison, because that is what makes the
 * two readable against each other. Opening it breaks it into its parts, since an address is
 * wrong a field at a time and correcting the postal code should not mean retyping the street.
 *
 * The value belongs to the panel, not to this field, because the field is not the only thing
 * that writes it: filling the form in from one of the customer's stored addresses has to land
 * here too.
 */
function EditableAddress({
  value,
  onCommit,
  readOnly = false,
}: {
  value: DeliveryAddress;
  onCommit?: (address: DeliveryAddress) => void;
  /** An order already in the ERP shows its address and offers no way to change it. */
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  function set(key: keyof DeliveryAddress) {
    return (next: string) => setDraft((prev) => ({ ...prev, [key]: next }));
  }

  function commit() {
    setEditing(false);
    onCommit?.(draft);
  }

  function cancel() {
    setEditing(false);
  }

  if (readOnly) {
    return (
      <div className="-ml-1 flex w-full px-1 py-0.5">
        <AddressLines address={value} />
      </div>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        aria-label="Edit delivery address"
        className="-ml-1 flex w-full rounded px-1 py-0.5 text-left hover:bg-gray-50"
      >
        <AddressLines address={value} />
      </button>
    );
  }

  return (
    <div
      className="space-y-1"
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") cancel();
      }}
    >
      <AddressField
        value={draft.company_name}
        onChange={set("company_name")}
        placeholder="Name"
        label="delivery name"
      />
      <AddressField
        value={draft.care_of}
        onChange={set("care_of")}
        placeholder="c/o"
        label="c/o"
      />
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">
          <AddressField
            value={draft.street}
            onChange={set("street")}
            placeholder="Street"
            label="street"
          />
        </div>
        <div className="w-16 shrink-0">
          <AddressField
            value={draft.house_number}
            onChange={set("house_number")}
            placeholder="No."
            label="house number"
          />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-24 shrink-0">
          <AddressField
            value={draft.postal_code}
            onChange={set("postal_code")}
            placeholder="Postal code"
            label="postal code"
          />
        </div>
        <div className="min-w-0 flex-1">
          <AddressField
            value={draft.city}
            onChange={set("city")}
            placeholder="City"
            label="city"
          />
        </div>
      </div>
      <AddressField
        value={draft.country}
        onChange={set("country")}
        placeholder="Country"
        label="country"
      />
      <AddressField
        value={draft.additional_information}
        onChange={set("additional_information")}
        placeholder="Additional information"
        label="additional information"
      />
      <div className="flex items-center justify-end gap-1 pt-0.5">
        <button
          type="button"
          onClick={cancel}
          aria-label="Cancel editing delivery address"
          className="flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
        >
          <X className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={commit}
          aria-label="Confirm delivery address"
          className="flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
        >
          <Check className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/**
 * Cell geometry, kept together so the header and both tiers stay on the same gutters.
 *
 * The table is border-separate so each pair can carry its own outline; that means every cell
 * draws its own borders, and the header draws transparent ones to keep the columns lined up.
 */
// Every cell starts at the top of its line. A line can run to two rows of text — the document's
// description and whatever else it said about the line — and centring what sits beside that
// leaves a column of values drifting against its neighbours. Top-aligned, the table reads down
// the way the order's own fields do.
const CELL = "px-4 py-2 align-top";
const FIRST_CELL = "pl-5 pr-4 py-2 align-top";
const LAST_CELL = "pl-4 pr-5 py-2 align-top";

/**
 * The elastic column.
 *
 * `w-full max-w-0` is what lets one cell of an auto-width table truncate: the table hands it
 * every pixel the identifier columns did not take, and the zero max-width gives the inner block
 * something definite to ellipsis against. Without it a long description either stretches the
 * table past its container or refuses to truncate at all.
 */
const FLEX_CELL = "w-full max-w-0 px-4 py-2 align-top";

/**
 * The document's own row: tinted, which is what marks the top of a pair.
 *
 * No rule above it any more. The pairs are held apart by a gap now, and a line inside that gap
 * only drew attention to the join it was already obvious at.
 */
const READ = "bg-gray-50";
/**
 * What one row is worth: the amount the document gave for it, or the price times the quantity
 * where it gave those instead. Null where it gave neither, which makes the sum incomplete rather
 * than wrong.
 */
function rowAmount(row: CustomerOrderRow): number | null {
  if (row.row_amount !== null) return row.row_amount;
  if (row.unit_price !== null && row.quantity !== null)
    return row.unit_price * row.quantity;
  return null;
}

type TotalsCheck =
  | { state: "no-order-total" }
  | { state: "no-row-amounts" }
  | { state: "match"; rowsTotal: number }
  | { state: "mismatch"; rowsTotal: number };

/**
 * Whether the rows add up to what the customer said the order came to.
 *
 * A document can state a total without tax, with it, or both, and we do not know its tax rate.
 * So the rows are held against every total it did state, and only a sum that meets none of them
 * is a disagreement. This is a sanity check on our reading of the rows, not an audit: the
 * likeliest explanations for a real disagreement are a row we never extracted and a quantity we
 * read wrong.
 *
 * The tolerance grows with the number of rows, because a document rounds each row to two decimals
 * and then rounds the total, so a long order can be a few öre out with nothing wrong.
 */
function checkTotals(
  rows: CustomerOrderRow[],
  totals: StatedTotals,
): TotalsCheck {
  const stated = [
    totals.total_order_price_excluding_taxes,
    totals.total_order_price_including_taxes,
  ].filter((total): total is number => total !== null);

  if (stated.length === 0) return { state: "no-order-total" };

  const amounts = rows.map(rowAmount);
  const known = amounts.filter((amount): amount is number => amount !== null);
  if (known.length === 0) return { state: "no-row-amounts" };

  const rowsTotal = known.reduce((sum, amount) => sum + amount, 0);
  const tolerance = Math.max(0.01, rows.length * 0.01);

  const meetsOne = stated.some(
    (total) => Math.abs(rowsTotal - total) <= tolerance,
  );
  return meetsOne
    ? { state: "match", rowsTotal }
    : { state: "mismatch", rowsTotal };
}

/**
 * The total the document stated, at the foot of the table.
 *
 * It is the extracted value and nothing else. The rows are summed only to decide whether to
 * colour it: red where the two disagree, since that usually means a row we never extracted or a
 * quantity we read wrong.
 *
 * Where the document stated no total, the field says so rather than showing what the rows add up
 * to. A computed figure in that slot would read as the customer's and hide the fact that we never
 * captured theirs.
 */
function OrderTotal({
  rows,
  totals,
}: {
  rows: CustomerOrderRow[];
  totals: StatedTotals;
}) {
  const check = checkTotals(rows, totals);
  const mismatch = check.state === "mismatch";
  const total = statedTotal(totals);

  const explanation = mismatch
    ? `The rows add up to ${formatAmount(check.rowsTotal)}.`
    : undefined;

  return (
    <div
      className="flex items-baseline justify-end gap-3 border-t border-gray-200 px-4 py-3"
      title={explanation}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Total
      </span>
      {total === null ? (
        <span className="text-sm text-gray-400">No total</span>
      ) : (
        <span
          className={cn(
            "text-lg font-semibold tabular-nums tracking-tight",
            mismatch ? "text-red-600" : "text-gray-900",
          )}
        >
          {formatAmount(total)}
        </span>
      )}
    </div>
  );
}

/** Our article for a row: the number we will order under and the description that comes with it. */
interface RowArticle {
  number: string;
  description: string;
}

function articlesFromRows(
  rows: CustomerOrderRow[],
): Record<string, RowArticle> {
  return Object.fromEntries(
    rows.map((row) => [
      row.id,
      {
        number: row.matched_article_number,
        description: row.matched_article_description,
      },
    ]),
  );
}

/**
 * Every value the form can change, as one flat map of field to value.
 *
 * The form is a page of small components that each hold the value they are editing, and lifting
 * all of that into one object would be a rewrite of the page. So each field reports what it
 * committed under the key it owns, and the page compares those reports against this same map
 * built from what the backend returned. A field nobody has touched is simply absent, and an edit
 * taken back to what the backend said stops differing on its own.
 *
 * The keys are the names the API takes, so the same map that answers "is anything unsaved" is
 * also what a save is built from, with no second translation to keep in step.
 */
function serverFields(detail: CustomerOrderDetail): Record<string, string> {
  const head = detail.customer_order_head;
  const address = addressFromHead(head);

  const fields: Record<string, string> = {
    [CUSTOMER_FIELD]: detail.customer?.number ?? "",
    "head.customer_order_number": head?.customer_order_number ?? "",
    "head.customer_contact_person": head?.customer_contact_person ?? "",
  };

  for (const [part, field] of ADDRESS_FIELDS) {
    fields[field] = address[part];
  }

  for (const row of detail.customer_order_rows) {
    fields[rowField(row.id, "position")] = row.position;
    fields[rowField(row.id, "article_number")] = row.matched_article_number;
    fields[rowField(row.id, "requested_delivery_date")] =
      formatDate(row.requested_delivery_date) ?? "";
    fields[rowField(row.id, "quantity")] =
      row.quantity === null ? "" : String(row.quantity);
  }

  return fields;
}

/** The customer the order points at, which is the order's own field rather than the head's. */
const CUSTOMER_FIELD = "customer_number";
/** The address is edited as one block and stored as seven columns. This is the seam. */
const ADDRESS_FIELDS: Array<[keyof DeliveryAddress, string]> = [
  ["company_name", "head.delivery_address_company_name"],
  ["care_of", "head.delivery_address_care_of"],
  ["street", "head.delivery_address_street"],
  ["house_number", "head.delivery_address_house_number"],
  ["postal_code", "head.delivery_address_postal_code"],
  ["city", "head.delivery_address_city"],
  ["country", "head.delivery_address_country"],
  ["additional_information", "head.delivery_address_additional_information"],
];

function rowField(rowId: string, name: string): string {
  return `row.${rowId}.${name}`;
}

/**
 * The edits, as the save endpoint takes them: only the fields that differ from what the backend
 * returned, so a save cannot carry a field the user never touched over somebody else's change.
 */
function draftFromEdits(
  edits: Record<string, string>,
  serverValues: Record<string, string>,
): CustomerOrderDraft {
  const draft: CustomerOrderDraft = {};
  const rows = new Map<string, CustomerOrderRowDraft>();

  for (const [field, value] of Object.entries(edits)) {
    if ((serverValues[field] ?? "") === value) continue;

    if (field === CUSTOMER_FIELD) {
      draft.customer_number = value;
      continue;
    }

    if (field.startsWith("head.")) {
      draft.head = { ...draft.head, [field.slice("head.".length)]: value };
      continue;
    }

    const [, rowId, name] = field.split(".");
    const row = rows.get(rowId) ?? { id: rowId };
    rows.set(rowId, { ...row, [name]: value });
  }

  if (rows.size > 0) draft.rows = Array.from(rows.values());
  return draft;
}

/**
 * The save that precedes a create failed, so the order was never sent.
 *
 * Worth its own type only because the two failures want different words: one leaves unsaved work
 * on screen, the other leaves saved work that the ERP would not take.
 */
class DraftNotSavedError extends Error {}

/** What the footer says about the form. Null while there is nothing to report. */
type SaveState = "unsaved" | "saved" | null;

/**
 * How long a stock reading is worth trusting before we ask again.
 *
 * Stock is not ours and moves without us, so what is held here is a reading with a time on it
 * rather than a fact. Long enough that working down an order does not re-ask on every render,
 * short enough that a number on screen is worth acting on. The control beside the table asks
 * again on demand, for when it is not.
 */
const STOCK_FRESH_FOR_MS = 5 * 60_000;

/**
 * Whether the ERP holds enough of an article for what was ordered.
 *
 * A darker ring around a brighter fill, so it reads as a lamp at 10px rather than as a dot of
 * text. Nothing is drawn when either number is unknown: an article we could not read stock for,
 * or a row with no quantity, has no answer to give, and a grey lamp would imply one.
 */
function StockLamp({
  inStock,
  demand,
}: {
  inStock: number | null | undefined;
  /** What the whole order asks of this article, not what this line asks. */
  demand: ArticleDemand | undefined;
}) {
  // Hovering shows the numbers; clicking pins them there, for reading a figure without holding
  // the pointer still or for a touch screen, which has no hover to offer.
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);

  if (inStock === null || inStock === undefined || demand === undefined)
    return null;

  const enough = inStock >= demand.quantity;

  return (
    <Popover
      open={hovered || pinned}
      onOpenChange={(open) => {
        if (!open) {
          setPinned(false);
          setHovered(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setHovered(true)}
          onBlur={() => setHovered(false)}
          onClick={() => setPinned((wasPinned) => !wasPinned)}
          aria-label={enough ? "Enough in stock" : "Not enough in stock"}
          className={cn(
            "h-2.5 w-2.5 shrink-0 rounded-full border",
            enough
              ? "border-emerald-600 bg-emerald-400"
              : "border-red-600 bg-red-400",
          )}
        />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-auto px-3 py-2"
        // A popover that opens on hover must not take the focus with it. Radix moves focus into
        // the content on open and hands it back to the trigger on close, which for a hover
        // target means pointing at a lamp steals the caret, and leaving it drags the caret to
        // the lamp from whatever field was clicked next.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <p className="text-xs font-medium text-gray-900">
          {enough ? "Enough in stock" : "Not enough in stock"}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {formatNumber(inStock)} in stock
        </p>
        <p className="text-xs text-gray-500">
          {formatNumber(demand.quantity)} ordered
          {/* Only worth saying when it is not this line alone: it explains a lamp that is red
              while the line beside it looks small enough to fit. */}
          {demand.rows > 1 && (
            <span className="text-gray-400"> across {demand.rows} rows</span>
          )}
        </p>
      </PopoverContent>
    </Popover>
  );
}

/**
 * What is wrong with our reading of a row, if anything.
 *
 * Red is something the order cannot be created without; amber is something that is there but
 * disagrees with the document. The same two meanings the order confirmation page gives them.
 */
type RowFlag = "missing" | "mismatch" | null;

function articleFlag(row: CustomerOrderRow, articleNumber: string): RowFlag {
  const matched = articleNumber.trim().toLowerCase();
  if (!matched) return "missing";

  const stated = [row.article_number, row.customer_article_number]
    .map((number) => number.trim().toLowerCase())
    .filter(Boolean);

  // A document that named no article cannot disagree with the one we found for it.
  if (stated.length === 0) return null;

  return stated.includes(matched) ? null : "mismatch";
}

// A missing article carries its own badge in the article cell, so its row stays white.
const ROW_FLAG_BACKGROUND: Record<Exclude<RowFlag, null>, string> = {
  missing: "bg-white",
  mismatch: "bg-amber-50",
};

/** Our row: no line of its own, so the two tiers read as one thing. */
const WRITE = "bg-white";

/**
 * A value of ours that can be corrected, shown as text until it is clicked.
 *
 * Reading the order is the common case and editing is the exception, so the fields stay out of
 * the way until asked for: a row of inputs reads as a form to fill in, which is the wrong
 * instruction when the pipeline already got it right. What it does keep is the invitation where
 * we have nothing, since an empty value still shows its placeholder.
 *
 * Hovering is the whole affordance. A pencil beside every value cost each of them its width and
 * clipped the longer ones, to say something the cursor already says.
 *
 * Nothing leaves the browser. There is no endpoint behind these yet, so a confirmed edit lives
 * in this component and goes no further.
 */
function EditableCell({
  initialValue,
  placeholder,
  label,
  onCommit,
  type = "text",
  align = "left",
  suffix,
  nowrap = false,
  readOnly = false,
}: {
  initialValue: string;
  placeholder: string;
  label: string;
  /** Called with the value the field settled on, so the page can tell the form from the order. */
  onCommit?: (value: string) => void;
  /** An order already in the ERP shows its values and offers no way to change them. */
  readOnly?: boolean;
  type?: "text" | "date" | "number";
  align?: "left" | "right";
  /** A unit belonging to the value, shown beside it rather than beside the field. */
  suffix?: string | null;
  /** Lets the value set the column's width instead of being clipped to it. */
  nowrap?: boolean;
}) {
  const [value, setValue] = useState(initialValue);
  const [draft, setDraft] = useState(initialValue);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
    setDraft(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function commit() {
    setValue(draft);
    setEditing(false);
    onCommit?.(draft);
  }

  function cancel() {
    setDraft(value);
    setEditing(false);
  }

  if (readOnly) {
    return (
      <span
        className={cn(
          "flex items-center px-1 py-0.5 text-sm",
          align === "right" ? "-mr-1 justify-end" : "-ml-1",
          nowrap ? "whitespace-nowrap" : "truncate",
          value ? "text-gray-900" : "text-gray-400",
        )}
      >
        {value || placeholder}
        {value && suffix ? (
          <span className="ml-1 text-gray-500">{suffix}</span>
        ) : null}
      </span>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") cancel();
          }}
          aria-label={label}
          className={cn(
            "h-6 min-w-0 rounded border border-input bg-white px-1.5 text-sm text-gray-900 shadow-sm outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/15",
            // A number needs room for a number. Stretching it across the column put the caret
            // a long way from the value and made the cell look like a different kind of field.
            align === "right" ? "w-16 shrink-0 text-right" : "flex-1",
          )}
        />
        <button
          type="button"
          onClick={cancel}
          aria-label={`Cancel editing ${label}`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
        >
          <X className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={commit}
          aria-label={`Confirm ${label}`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
        >
          <Check className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      aria-label={`Edit ${label}`}
      className={cn(
        // The padding is pulled back so a value lines up with the one on the tinted row above
        // it rather than sitting a few pixels inside its column.
        "flex items-center rounded px-1 py-0.5 text-left hover:bg-gray-50",
        align === "right" ? "-mr-1 w-auto justify-end" : "-ml-1 w-full",
      )}
    >
      <span
        className={cn(
          nowrap ? "whitespace-nowrap" : "truncate",
          "text-sm",
          value ? "text-gray-900" : "text-gray-400",
        )}
      >
        {value || placeholder}
        {value && suffix ? (
          <span className="ml-1 text-gray-500">{suffix}</span>
        ) : null}
      </span>
    </button>
  );
}

const PANEL_LABEL =
  "text-[10px] font-semibold uppercase tracking-wide text-gray-500"; // same as the purchase order heads

/** One field of the head. What the document said for it lives in the panel's own popover. */
function HeadField({
  label,
  action,
  children,
  className,
}: {
  label: string;
  /** Something the field offers, on the label's line: small, and out of the value's way. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("px-4 py-3", className)}>
      <div className="flex items-center justify-between gap-2">
        <p className={PANEL_LABEL}>{label}</p>
        {action}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

/**
 * Everything the extraction gave us, in one place.
 *
 * An icon on every field put five of them on screen to answer a question that gets asked about
 * one field at a time, and rarely. One icon on the panel opens the lot under their own headings,
 * which also lets the document be read as a document rather than field by field.
 */
function ExtractedValues({
  head,
  address,
}: {
  head: CustomerOrderHead | null | undefined;
  address: DeliveryAddress;
}) {
  const fields = [
    { label: "Order number", value: head?.customer_order_number },
    { label: "Customer", value: head?.customer_name },
    { label: "Delivery address", value: addressLines(address).join("\n") },
    { label: "Customer contact", value: head?.customer_contact_person },
  ].filter((field) => field.value?.trim());

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Show the extracted values"
          title="Show the extracted values"
          className="absolute right-4 top-4 text-gray-300 transition-colors hover:text-gray-500"
        >
          <FileText className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" side="bottom" className="w-80 p-0">
        <div className="border-b border-gray-100 px-4 py-2.5">
          <p className={PANEL_LABEL}>Extracted values</p>
        </div>
        {fields.length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-400">
            Nothing was extracted from this order.
          </p>
        ) : (
          <div className="space-y-3 px-4 py-3">
            {fields.map((field) => (
              <div key={field.label}>
                <p className={PANEL_LABEL}>{field.label}</p>
                <p className="mt-0.5 whitespace-pre-line text-sm text-gray-700">
                  {field.value}
                </p>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * A stored address as one line to choose by and one to tell it apart with.
 *
 * What an address is called is what it is known as inside the customer, and that is what someone
 * picking one is looking for. Where it is goes underneath, for the two warehouses in the same
 * town. An address with no name of its own is chosen by its street instead, and then there is
 * nothing left to put on the second line.
 */
function addressPresetOption(address: CustomerAddress): PresetOption {
  const street = [address.street, address.house_number]
    .filter(Boolean)
    .join(" ");
  const city = [address.postal_code, address.city].filter(Boolean).join(" ");
  const where = [street, city, address.country].filter(Boolean).join(", ");
  const name = address.company_name.trim();

  return {
    id: address.id,
    title: name || where || "Unnamed address",
    detail: name ? where : undefined,
  };
}

/** A stored contact, by name, with whatever there is of a way to reach them underneath. */
function contactPersonPresetOption(person: CustomerContactPerson): PresetOption {
  return {
    id: person.id,
    title: person.name,
    detail:
      [person.email_address, person.phone_number].filter(Boolean).join(", ") ||
      undefined,
  };
}

/**
 * The head, as the order we are about to create.
 *
 * Only what goes into creating the order and pointing it at an ERP customer appears here. The
 * document carries more than that, and showing the rest would give a salesperson things to read
 * that no decision hangs on.
 *
 * The customer is named by number and name rather than by address, because an ERP customer is not
 * guaranteed to have one. The address is the customer's, carried across from the extraction, since
 * that is where the goods are actually going.
 */
function OrderHeadPanel({
  detail,
  onFieldChange,
  readOnly,
}: {
  detail: CustomerOrderDetail;
  onFieldChange: (field: string, value: string) => void;
  /** An order already in the ERP is shown, not edited. */
  readOnly: boolean;
}) {
  const head = detail.customer_order_head;
  const matched = detail.customer;
  const savedAddress = useMemo(() => addressFromHead(head), [head]);

  // The id, the number and the name move together: picking a different customer has to replace
  // all three, so they are held here rather than inside the field. The id is what the customer's
  // addresses and contacts are read by, and it is the typeahead that hands it over.
  const [customer, setCustomer] = useState({
    id: matched?.id ?? "",
    number: matched?.number ?? "",
    name: matched?.name ?? "",
  });

  // The address and the contact are held here for the same reason, and because the field is no
  // longer the only thing that writes them: either can also be filled in from what the ERP holds
  // for the customer. They follow the server again whenever it answers with something else.
  const [address, setAddress] = useState(savedAddress);
  const [contactPerson, setContactPerson] = useState(
    head?.customer_contact_person ?? "",
  );

  useEffect(() => {
    setCustomer({
      id: matched?.id ?? "",
      number: matched?.number ?? "",
      name: matched?.name ?? "",
    });
  }, [matched?.id, matched?.number, matched?.name]);

  useEffect(() => {
    setAddress(savedAddress);
  }, [savedAddress]);

  useEffect(() => {
    setContactPerson(head?.customer_contact_person ?? "");
  }, [head?.customer_contact_person]);

  // Keyed on the customer the form points at rather than the one the order was saved against, so
  // choosing a different company offers that company's addresses and contacts straight away. The
  // matched customer came with the order, so the first read is free.
  const presets = useCustomerDetail(detail.workflow_id, customer.id, matched);

  function changeAddress(next: DeliveryAddress) {
    setAddress(next);
    for (const [part, field] of ADDRESS_FIELDS) onFieldChange(field, next[part]);
  }

  function fillAddressFrom(presetId: string) {
    const preset = presets.data?.addresses.find(
      (option) => option.id === presetId,
    );
    if (!preset) return;
    changeAddress({
      company_name: preset.company_name,
      // The ERP's own record of an address rarely carries a c/o, and ours is where the delivery
      // contact off the customer's document ends up. Taking an empty one from the preset would
      // drop the one part of this address the document knew and the ERP did not.
      care_of: preset.care_of.trim() || address.care_of,
      street: preset.street,
      house_number: preset.house_number,
      postal_code: preset.postal_code,
      city: preset.city,
      country: preset.country,
      additional_information: preset.additional_information,
    });
  }

  function fillContactPersonFrom(presetId: string) {
    const preset = presets.data?.contact_persons.find(
      (option) => option.id === presetId,
    );
    if (!preset) return;
    setContactPerson(preset.name);
    onFieldChange("head.customer_contact_person", preset.name);
  }

  return (
    <Container className="relative min-w-0">
      <ExtractedValues head={head} address={savedAddress} />

      {/* Two to a row: none of these is wide, and four full-width rows made the head a column of
          mostly empty space above the table that matters. */}
      <div className="grid grid-cols-1 sm:grid-cols-2">
        {/*
          Who the order is from and what they called it, side by side at the top. Where it goes,
          and who to ask about it, follow underneath.
        */}
        <HeadField label="Customer">
          {/*
            Number over name, both at the same size: they are one identity, and the whole block
            is the target, so hovering either offers to replace both.
          */}
          <ErpTypeahead
            readOnly={readOnly}
            value={customer.number}
            onPick={(suggestion) => {
              setCustomer({
                id: suggestion.id ?? "",
                number: suggestion.value,
                name: suggestion.description,
              });
              onFieldChange(CUSTOMER_FIELD, suggestion.value);
            }}
            placeholder="No customer"
            label="customer"
            queryKey={["erp-customer-search", detail.workflow_id]}
            enabled={!!detail.workflow_id}
            iconClassName="h-4 w-4"
            renderResting={(number) => (
              <span className="min-w-0 flex-1 text-lg font-semibold leading-snug tracking-tight text-gray-900">
                <span className="block truncate">
                  {number || (
                    <span className="font-normal text-red-600">
                      No customer
                    </span>
                  )}
                </span>
                {customer.name && (
                  <span className="block truncate">{customer.name}</span>
                )}
              </span>
            )}
            search={async (query, source) => {
              const results = await searchErpCustomers(
                detail.workflow_id,
                query,
                source,
              );
              return results.map((result) => ({
                id: result.id,
                value: result.number,
                description: result.name,
              }));
            }}
          />
        </HeadField>

        {/* Padded clear of the panel's icon so nothing can run under it. */}
        <HeadField label="Order number" className="sm:pr-12">
          <EditableCell
            readOnly={readOnly}
            initialValue={head?.customer_order_number ?? ""}
            placeholder="No order number"
            label="order number"
            onCommit={(value) =>
              onFieldChange("head.customer_order_number", value)
            }
          />
        </HeadField>

        {/*
          The address stays a form. What the ERP is sent is whatever is written here, and the
          customer's stored addresses are offered beside it as a way of filling it in rather than
          as something to choose between: picking one writes it into these fields and leaves them
          as editable as they were.
        */}
        <HeadField
          label="Delivery address"
          action={
            readOnly ? null : (
              <UseSaved
                label="delivery address"
                options={(presets.data?.addresses ?? []).map(
                  addressPresetOption,
                )}
                loading={presets.isLoading}
                emptyReason={
                  customer.number
                    ? "No addresses stored for this customer"
                    : "Match a customer to see its addresses"
                }
                onPick={fillAddressFrom}
              />
            )
          }
        >
          <EditableAddress
            readOnly={readOnly}
            value={address}
            onCommit={changeAddress}
          />
        </HeadField>

        <HeadField
          label="Customer contact"
          action={
            readOnly ? null : (
              <UseSaved
                label="customer contact"
                options={(presets.data?.contact_persons ?? []).map(
                  contactPersonPresetOption,
                )}
                loading={presets.isLoading}
                emptyReason={
                  customer.number
                    ? "No contacts stored for this customer"
                    : "Match a customer to see its contacts"
                }
                onPick={fillContactPersonFrom}
              />
            )
          }
        >
          <EditableCell
            readOnly={readOnly}
            initialValue={contactPerson}
            placeholder="No customer contact"
            label="customer contact"
            onCommit={(value) => {
              setContactPerson(value);
              onFieldChange("head.customer_contact_person", value);
            }}
          />
        </HeadField>
      </div>
    </Container>
  );
}

/**
 * One line of the order: what the document said, and what we made of it.
 *
 * The two are drawn as one outlined block so it is never in doubt which reading belongs to which
 * row, and they share the table's columns so the comparison is a downward read. The document's
 * half is tinted and fixed; ours can be corrected where the pipeline was wrong.
 */
function RowLines({
  row,
  workflowId,
  article,
  onPickArticle,
  onFieldChange,
  stock,
  demand,
  first,
  readOnly,
  onDelete,
}: {
  row: CustomerOrderRow;
  workflowId: string;
  /** Our article for this row. Held by the panel, because the stock query is keyed on the set. */
  article: RowArticle;
  onPickArticle: (rowId: string, article: RowArticle) => void;
  onFieldChange: (field: string, value: string) => void;
  stock: Map<string, number | null>;
  demand: Map<string, ArticleDemand>;
  /** The first pair needs no gap above it: the header already ends the run. */
  first: boolean;
  /** An order already in the ERP is shown, not edited. */
  readOnly: boolean;
  /** Asks for the row to be dropped. The page confirms it; this only says which row. */
  onDelete: (row: CustomerOrderRow) => void;
}) {
  const requestedDate = formatDate(row.requested_delivery_date);
  const unit = row.unit || null;
  const flag = articleFlag(row, article.number);
  const write = flag ? ROW_FLAG_BACKGROUND[flag] : WRITE;
  const addedByHand = row.source === "user";

  return (
    <>
      {/*
        The gap that makes a pair a pair. It is a row of its own rather than padding on either
        half, because padding would grow one of the two backgrounds instead of leaving white
        between them — which is the whole point.
      */}
      {!first && (
        <tr aria-hidden>
          <td colSpan={readOnly ? 5 : 6} className="h-3" />
        </tr>
      )}
      {/*
        The top half of a pair is what the customer's document said. A line somebody added by
        hand has no such half: the article and description on it are ours, and laying them out
        in these columns would read as the document having stated them. So it says where the
        line came from instead, and the half below it carries the row as normal.
      */}
      {addedByHand ? (
        <tr>
          <td
            colSpan={readOnly ? 5 : 6}
            className={`${FIRST_CELL} ${READ} whitespace-nowrap text-xs text-gray-400`}
          >
            Added by user. Not from original order document.
          </td>
        </tr>
      ) : (
        <tr>
          <td
            className={`${FIRST_CELL} ${READ} whitespace-nowrap text-xs text-gray-500`}
          >
            {row.position || <span className="text-gray-400">-</span>}
          </td>
          <td
            className={`${CELL} ${READ} whitespace-nowrap text-xs text-gray-500`}
          >
            {/* The customer's own number sits lighter beside ours, spaced rather than punctuated. */}
            {row.article_number && <span>{row.article_number}</span>}
            {row.customer_article_number && (
              <span
                className={
                  row.article_number ? "ml-3 text-gray-400" : undefined
                }
              >
                {row.customer_article_number}
              </span>
            )}
          </td>
          <td className={`${FLEX_CELL} ${READ} text-xs text-gray-500`}>
            <span
              className="block truncate"
              title={row.article_description || undefined}
            >
              {row.article_description}
            </span>
            {/*
            Whatever else the document said about this line, under the description it belongs to.
            It is something the customer wrote, so it reads with the rest of what they wrote
            rather than under the row we are building from it.
          */}
            {row.additional_information?.trim() && (
              <span
                className="block truncate text-gray-400"
                title={row.additional_information}
              >
                {row.additional_information}
              </span>
            )}
          </td>
          {/*
          The date the pipeline read off the document. Its absence is stated rather than left
          blank: a row the customer gave no date for is a thing the salesperson has to settle,
          not an empty cell to scroll past.
        */}
          <td
            className={`${CELL} ${READ} whitespace-nowrap text-xs text-gray-500`}
          >
            {requestedDate ?? (
              <span className="text-gray-400">No delivery date</span>
            )}
          </td>
          <td
            className={`${LAST_CELL} ${READ} whitespace-nowrap text-right text-xs text-gray-500`}
          >
            {formatNumber(row.quantity)}
            {row.quantity !== null && unit ? (
              <span className="ml-1">{unit}</span>
            ) : null}
          </td>
          {/* The control belongs to the row we are building, not to what the document said. */}
          {!readOnly && <td className={`${READ} w-8`} />}
        </tr>
      )}

      <tr className="group/row">
        <td className={`${FIRST_CELL} ${write}`}>
          <EditableCell
            readOnly={readOnly}
            initialValue={row.position}
            placeholder="-"
            label="position"
            onCommit={(value) =>
              onFieldChange(rowField(row.id, "position"), value)
            }
          />
        </td>
        <td className={`${CELL} ${write} min-w-[9rem]`}>
          <ErpTypeahead
            readOnly={readOnly}
            value={article.number}
            onPick={(suggestion) =>
              onPickArticle(row.id, {
                number: suggestion.value,
                description: suggestion.description,
              })
            }
            placeholder="No article"
            label="article"
            tone={
              flag === "missing"
                ? "missing"
                : flag === "mismatch"
                  ? "deviating"
                  : "default"
            }
            queryKey={["erp-article-search", workflowId]}
            enabled={!!workflowId}
            search={async (query, source) => {
              const results = await searchErpArticles(
                workflowId,
                query,
                source,
              );
              return results.map((result) => ({
                value: result.article_number,
                description: result.description,
              }));
            }}
          />
        </td>
        {/*
          The description belongs to the article and is never typed here: correcting it means
          pointing the row at a different article, which the search to its left does.

          The lamp leads it rather than taking a column of its own: it is a fact about the article
          this line names, and the description is the one part of the row with space to spare.
        */}
        <td className={`${FLEX_CELL} ${write} text-sm text-gray-700`}>
          <span className="flex items-center gap-1.5">
            <StockLamp
              inStock={stock.get(article.number)}
              demand={demand.get(article.number)}
            />
            <span
              className="min-w-0 truncate"
              title={article.description || undefined}
            >
              {article.description}
            </span>
          </span>
        </td>
        <td className={`${CELL} ${write} min-w-[8rem]`}>
          <EditableCell
            readOnly={readOnly}
            initialValue={requestedDate ?? ""}
            placeholder="No delivery date"
            label="delivery date"
            type="date"
            nowrap
            onCommit={(value) =>
              onFieldChange(rowField(row.id, "requested_delivery_date"), value)
            }
          />
        </td>
        <td className={`${LAST_CELL} ${write} whitespace-nowrap text-right`}>
          <EditableCell
            readOnly={readOnly}
            initialValue={row.quantity === null ? "" : String(row.quantity)}
            placeholder="No quantity"
            label="quantity"
            type="number"
            align="right"
            suffix={unit}
            onCommit={(value) =>
              onFieldChange(rowField(row.id, "quantity"), value)
            }
          />
        </td>
        {/*
          Quiet until the row is under the pointer: dropping a line is rare and irreversible, and
          a bin on every row draws the eye to the one action nobody came here to take.
        */}
        {!readOnly && (
          <td className={`${write} w-8 align-middle`}>
            <button
              type="button"
              onClick={() => onDelete(row)}
              aria-label={`Remove row ${row.position || row.article_number}`}
              title="Remove this row"
              className="rounded p-1 text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover/row:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </td>
        )}
      </tr>
    </>
  );
}

function OrderRowsPanel({
  detail,
  articles,
  onPickArticle,
  onFieldChange,
  demand,
  readings,
  readOnly,
  onDeleteRow,
}: {
  detail: CustomerOrderDetail;
  articles: Record<string, RowArticle>;
  onPickArticle: (rowId: string, article: RowArticle) => void;
  onFieldChange: (field: string, value: string) => void;
  demand: Map<string, ArticleDemand>;
  readings: ReturnType<typeof useStockReadings>;
  /** An order already in the ERP is shown, not edited. */
  readOnly: boolean;
  onDeleteRow: (row: CustomerOrderRow) => void;
}) {
  const rows = detail.customer_order_rows;
  const headerCell = "border-y border-x border-transparent";

  const { stock, hasArticles, isFetching, isError, dataUpdatedAt, refetch } =
    readings;

  return (
    <Container className="min-w-0 overflow-hidden">
      {rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-gray-400">
          No line items.
        </p>
      ) : (
        <div>
          {hasArticles && (
            <div className="flex items-center justify-end gap-2 px-4 pt-3 text-xs text-gray-400">
              <span>
                {isFetching
                  ? "Reading stock…"
                  : isError
                    ? "Could not read stock"
                    : dataUpdatedAt
                      ? `Stock as of ${new Date(dataUpdatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                      : "Stock not read"}
              </span>
              <button
                type="button"
                onClick={() => void refetch()}
                disabled={isFetching}
                aria-label="Read stock again"
                title="Read stock again"
                className="rounded p-0.5 text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500 disabled:opacity-40"
              >
                <RefreshCw
                  className={cn("h-3 w-3", isFetching && "animate-spin")}
                />
              </button>
            </div>
          )}
          <table className="w-full table-auto border-separate border-spacing-0">
            <thead>
              <tr className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <th
                  className={`${FIRST_CELL} ${headerCell} whitespace-nowrap text-left font-semibold`}
                >
                  Pos
                </th>
                <th
                  className={`${CELL} ${headerCell} whitespace-nowrap text-left font-semibold`}
                >
                  Article
                </th>
                <th
                  className={`${FLEX_CELL} ${headerCell} text-left font-semibold`}
                >
                  Description
                </th>
                <th
                  className={`${CELL} ${headerCell} whitespace-nowrap text-left font-semibold`}
                >
                  Delivery
                </th>
                <th
                  className={`${LAST_CELL} ${headerCell} whitespace-nowrap text-right font-semibold`}
                >
                  Qty
                </th>
                {/* The control's column carries no heading: there is nothing to call it. */}
                {!readOnly && <th className={`${headerCell} w-8`} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <RowLines
                  readOnly={readOnly}
                  onDelete={onDeleteRow}
                  key={row.id}
                  first={index === 0}
                  row={row}
                  workflowId={detail.workflow_id}
                  article={articles[row.id] ?? { number: "", description: "" }}
                  onPickArticle={onPickArticle}
                  onFieldChange={onFieldChange}
                  stock={stock}
                  demand={demand}
                />
              ))}
            </tbody>
          </table>

          <OrderTotal
            rows={rows}
            totals={{
              total_order_price_excluding_taxes:
                detail.customer_order_head?.total_order_price_excluding_taxes ??
                null,
              total_order_price_including_taxes:
                detail.customer_order_head?.total_order_price_including_taxes ??
                null,
            }}
          />
        </div>
      )}
    </Container>
  );
}

/**
 * What the order's internal status means to a salesperson.
 *
 * The pipeline's own vocabulary is not the user's: "open" is the queue they work, "exists" means
 * the ERP already had the order so there is nothing left to do. Anything not listed here is a
 * state no block writes today, and shows as itself rather than being dressed up as something
 * this page understands.
 */
const CUSTOMER_ORDER_STATUS = {
  open: {
    label: SALES_ORDER_METRIC_COPY.awaiting_validation.label,
    Icon: Inbox,
    className: "border-blue-300 bg-blue-50 text-blue-800",
  },
  exists: {
    label: SALES_ORDER_METRIC_COPY.done.label,
    Icon: CheckSquare,
    className: "border-emerald-300 bg-emerald-50 text-emerald-800",
  },
  created: {
    label: SALES_ORDER_METRIC_COPY.done.label,
    Icon: CheckSquare,
    className: "border-emerald-300 bg-emerald-50 text-emerald-800",
  },
  error: {
    label: "Error",
    Icon: AlertTriangle,
    className: "border-amber-300 bg-amber-50 text-amber-800",
  },
  initialized: {
    label: "Processing",
    Icon: Clock,
    className: "border-canvas-border/60 bg-surface text-gray-600",
  },
} as const;

function CustomerOrderStatusBadge({ status }: { status: string }) {
  const known =
    CUSTOMER_ORDER_STATUS[status as keyof typeof CUSTOMER_ORDER_STATUS];
  const { label, Icon, className } = known ?? {
    label: status,
    Icon: Clock,
    className: "border-canvas-border/60 bg-surface text-gray-600",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium shadow-panel",
        className,
      )}
    >
      <Icon
        className="h-4 w-4 shrink-0 opacity-75"
        strokeWidth={2}
        aria-hidden
      />
      {label}
    </span>
  );
}

/**
 * The stock reading for a set of articles, held with the time it was taken.
 *
 * A hook rather than a query in one component, because two places need the same reading: the
 * table, which draws a lamp per row, and the header, which says whether any of them is short.
 * Asking twice would be two requests for one answer, and two answers that could disagree.
 *
 * `checkStock` is the order's own setting. Off means the ERP is never asked, and the reading it
 * returns stays empty — which is also what keeps the lamps off the rows, since a row with no
 * reading draws nothing either way.
 */
function useStockReadings(
  workflowId: string,
  articles: Record<string, RowArticle>,
  checkStock: boolean,
) {
  // Sorted and deduplicated so the key is the set of articles, not the order they appear in:
  // re-reading the same order must hit the same cache entry.
  const articleNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(articles)
            .map((a) => a.number)
            .filter(Boolean),
        ),
      ).sort(),
    [articles],
  );

  const query = useQuery({
    queryKey: ["erp-inventory", workflowId, articleNumbers],
    queryFn: () => getErpInventory(workflowId, articleNumbers),
    enabled: checkStock && articleNumbers.length > 0,
    // Within the window the reading already taken is used; after it, the next render asks again,
    // and the control beside the table asks on demand.
    staleTime: STOCK_FRESH_FOR_MS,
    gcTime: 30 * 60_000,
  });

  const stock = useMemo(
    () =>
      new Map(
        (query.data ?? []).map((entry) => [
          entry.article_number,
          entry.quantity,
        ]),
      ),
    [query.data],
  );

  return {
    ...query,
    stock,
    hasArticles: checkStock && articleNumbers.length > 0,
  };
}

/** What one article is being asked for across the whole order, and over how many lines. */
interface ArticleDemand {
  quantity: number;
  rows: number;
}

/**
 * What the order asks of each article, added up.
 *
 * Stock is held per article, not per line, so a line is not the thing to compare against it: two
 * lines of five, differing only in when they are wanted, are ten against the same balance. Asked
 * a line at a time, both would read as covered by a stock of six.
 *
 * The quantity counted is the one in the form, not the one the document was read as saying. The
 * form is what will be ordered, so it is what the stock has to cover — a line corrected from five
 * to fifty is short before it is saved, not after.
 *
 * A line with no article, or none with a quantity, asks for nothing and is left out — it has
 * nothing to add and would otherwise report a demand of zero as though it were satisfied.
 */
function articleDemand(
  rows: CustomerOrderRow[],
  articles: Record<string, RowArticle>,
  edits: Record<string, string>,
): Map<string, ArticleDemand> {
  const demand = new Map<string, ArticleDemand>();

  for (const row of rows) {
    const number = (articles[row.id]?.number ?? "").trim();
    const quantity = orderedQuantity(row, edits);
    if (!number || quantity === null) continue;

    const running = demand.get(number) ?? { quantity: 0, rows: 0 };
    demand.set(number, {
      quantity: running.quantity + quantity,
      rows: running.rows + 1,
    });
  }

  return demand;
}

/**
 * The quantity a line will be ordered for: what has been typed into it, or what the document was
 * read as saying where nothing has been. A field emptied, or typed into so far as to be no number
 * at all, orders nothing rather than zero.
 */
function orderedQuantity(
  row: CustomerOrderRow,
  edits: Record<string, string>,
): number | null {
  const edited = edits[rowField(row.id, "quantity")];
  if (edited === undefined) return row.quantity;

  const trimmed = edited.trim();
  if (trimmed === "") return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * How many of the order's articles the ERP holds enough of, out of the articles it can answer for.
 *
 * An article can be asked about when the order wants a quantity of it and the ERP answered for
 * it. The ones it could not answer for are left out of both figures rather than counted as short,
 * so the denominator is the question actually answered. Null where nothing can answer, which
 * keeps the header quiet rather than reporting nothing out of nothing.
 */
function stockCounts(
  demand: Map<string, ArticleDemand>,
  stock: Map<string, number | null>,
): { enough: number; answered: number } | null {
  let answered = 0;
  let enough = 0;

  for (const [number, ordered] of demand) {
    const inStock = stock.get(number);
    if (inStock === undefined || inStock === null) continue;

    answered += 1;
    if (inStock >= ordered.quantity) enough += 1;
  }

  if (answered === 0) return null;
  return { enough, answered };
}

/**
 * One figure about the order, in the pill the order confirmation page uses for the same purpose.
 */
function StatPill({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-h-[2.5rem] min-w-[8.5rem] flex-col justify-center rounded-chip border border-canvas-border/60 bg-surface px-3.5 shadow-panel py-1.5 text-gray-700">
      <div className="flex w-full items-baseline justify-between gap-3">
        <p className="text-xs font-medium text-current/80">{label}</p>
        <p
          className={cn(
            "text-sm font-medium leading-none tabular-nums",
            valueClassName ?? "text-current",
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

/**
 * How much of the order we have settled, at the top of the page.
 *
 * Counted from the articles on screen rather than the ones the pipeline stored, so picking one
 * moves the count as it moves the lamp and the row's colour.
 *
 * The missing count is hidden at zero, as the deviation counts are on the order confirmation
 * page: a row of zeroes says nothing, and an order with nothing missing should say so by the
 * matched figures meeting rather than by a red nought.
 */
function RowStats({
  rows,
  articles,
  stock,
  demand,
  checkStock,
}: {
  rows: CustomerOrderRow[];
  articles: Record<string, RowArticle>;
  stock: Map<string, number | null>;
  demand: Map<string, ArticleDemand>;
  /** The order's own setting. Off leaves the stock figure out rather than showing it as unknown. */
  checkStock: boolean;
}) {
  const total = rows.length;
  const matched = rows.filter(
    (row) => (articles[row.id]?.number ?? "").trim() !== "",
  ).length;
  const missing = total - matched;
  const inStock = checkStock ? stockCounts(demand, stock) : null;

  if (total === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <StatPill
        label="Matched rows"
        value={`${matched}/${total}`}
        valueClassName="text-gray-900"
      />
      {missing > 0 && (
        <StatPill
          label="Rows missing"
          value={missing}
          valueClassName="text-red-600"
        />
      )}
      {/*
        One answer for the whole order, standing in for the row lamps. Counted per article rather
        than per row, because that is how stock is held and how the lamps read it: two lines of
        the same article are one question. It says nothing about matching, so it counts only what
        can answer it and stays away until something can.
      */}
      {inStock !== null && (
        <StatPill
          label="Articles in stock"
          value={`${inStock.enough}/${inStock.answered}`}
          valueClassName={
            inStock.enough < inStock.answered
              ? "text-red-600"
              : "text-emerald-700"
          }
        />
      )}
    </div>
  );
}

/**
 * What the form is, in the words next to the button that would settle it.
 *
 * Fine print rather than a warning: unsaved work on a page built for editing is the ordinary
 * case, not a problem, and the button waking up from grey is most of the notice. Nothing is said
 * until there is something to say — a page only read is neither unsaved nor saved, and "Saved" on
 * arrival would claim a save that never happened.
 */
function SaveStateLabel({ state }: { state: SaveState }) {
  if (state === "unsaved") {
    return <span className="text-xs text-gray-900">Unsaved changes</span>;
  }

  if (state === "saved") {
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-700">
        <Check className="h-3 w-3 shrink-0" />
        Saved
      </span>
    );
  }

  return null;
}

/**
 * The actions the page exists to end in.
 *
 * Fixed to the foot of the window the way the order confirmation page's is, so the decision is
 * reachable without scrolling back from the last row. Neither button does anything yet: there is
 * no endpoint behind either, and the page saves nothing.
 */
function ActionBar({
  saveState,
  onSaveDraft,
  canSaveDraft,
  saving,
  onManualHandling,
  onSkip,
  onCreateOrder,
  creating,
  actionsEnabled,
  sent,
}: {
  saveState: SaveState;
  onSaveDraft: () => void;
  canSaveDraft: boolean;
  saving: boolean;
  onManualHandling: () => void;
  onSkip: () => void;
  onCreateOrder: () => void;
  creating: boolean;
  /** An order that has left the queue can no longer be moved out of it. */
  actionsEnabled: boolean;
  /** An order already in the ERP. Nothing here can be done to it twice. */
  sent: boolean;
}) {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur-sm">
      {/* The label sits with the button it is about, not across the bar from it. */}
      <div className="container mx-auto flex items-center justify-end gap-3 py-3">
        <SaveStateLabel state={saveState} />
        <div className="flex items-center gap-2">
          {/* The two ways out of the queue that are not a sales order. Behind a menu because they
              are the rare answer, and beside the buttons because they end the same task. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                disabled={!actionsEnabled}
                aria-label="More actions"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top">
              <DropdownMenuItem onClick={onManualHandling}>
                Move to Manual Handling
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onSkip}
                className="text-red-600 focus:text-red-600"
              >
                Skip Order
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            variant="outline"
            disabled={!canSaveDraft || saving || creating || sent}
            onClick={onSaveDraft}
          >
            <Save className="mr-2 h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save Draft"}
          </Button>
          <Button
            type="button"
            variant="cta"
            disabled={creating || saving || sent}
            onClick={onCreateOrder}
          >
            {creating ? "Creating…" : "Save & Create Order"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CustomerOrderDetailPage() {
  const { workflowId, customerOrderId } = useParams<{
    workflowId: string;
    customerOrderId: string;
  }>();

  // Held at the page, because two things read it: the table, which edits it, and the counts in
  // the header, which have to move with a pick the same way the row's lamp and colour do.
  const [articles, setArticles] = useState<Record<string, RowArticle>>({});

  // What the form has been changed to, field by field, against what the backend last said.
  const [edits, setEdits] = useState<Record<string, string>>({});

  // Set by a confirmed save, and left alone by anything else: it is a statement about what
  // happened, not about how the form currently compares.
  const [saved, setSaved] = useState(false);

  // Whether the save in flight was asked for on the way out, which decides what happens after it.
  const [leavingAfterSave, setLeavingAfterSave] = useState(false);

  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [rowToDrop, setRowToDrop] = useState<CustomerOrderRow | null>(null);
  const [erpErrors, setErpErrors] = useState<string[]>([]);
  const [showErpErrors, setShowErpErrors] = useState(false);

  const queryClient = useQueryClient();

  function changeField(field: string, value: string) {
    setEdits((current) => ({ ...current, [field]: value }));
    setSaved(false);
  }

  const {
    data: detail,
    isLoading,
    isError,
  } = useQuery({
    queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
    queryFn: () => getCustomerOrderDetail(customerOrderId!),
    enabled: !!customerOrderId,
    ...salesOrderQueryOptions,
  });

  const salesOrderVersion = detail?.sales_order_version ?? null;

  // The page opens on whichever document is the record. Once the ERP has answered with its own
  // order that is its copy; before then there is only ours. It is state rather than a derived
  // value because the nav moves between them, and it is keyed on the order so that walking from
  // one order to the next does not carry the previous one's choice along.
  const [viewMode, setViewMode] = useState<OrderViewMode>("sales-order");
  useEffect(() => {
    setViewMode("sales-order");
  }, [customerOrderId]);

  // Resolved per order rather than per workflow: the order's customer can override the workflow's
  // settings. False while the order is still loading, which is why the reading is a hook and not
  // something the loaded page starts off.
  //
  // Read through the settings, not into them: an API that predates a field answers without it,
  // and a page that assumes every field it knows about is present breaks on the deploy gap.
  const checkStock = detail?.settings?.check_stock_balance ?? false;
  const readings = useStockReadings(workflowId ?? "", articles, checkStock);

  // Held at the page for the same reason the articles are: the lamps and the figure in the header
  // are one reading, and repointing a single row changes what every row sharing that article asks
  // for.
  const serverRows = detail?.customer_order_rows;
  const demand = useMemo(
    () => articleDemand(serverRows ?? [], articles, edits),
    [serverRows, articles, edits],
  );

  // Keyed on when the order was last written rather than on the response object, because the
  // query has no stale time: a refetch on window focus hands back a new object holding the same
  // values, and starting the form over on that would throw away what was typed while the tab was
  // in the background. A real change — someone else's save, or our own — moves last_updated.
  const lastUpdated = detail?.last_updated;
  useEffect(() => {
    if (!detail) return;
    setArticles(articlesFromRows(detail.customer_order_rows));
    setEdits({});
    // detail is deliberately not a dependency; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdated]);

  const serverValues = useMemo(
    () => (detail ? serverFields(detail) : {}),
    [detail],
  );

  // A field that was changed and changed back is not a change. Comparing values rather than
  // counting edits is what makes the indicator go quiet again when the form is put back.
  const hasUnsavedChanges = Object.entries(edits).some(
    ([field, value]) => (serverValues[field] ?? "") !== value,
  );

  // Held here rather than at the action bar: it is the page that has unsaved work, and the
  // question has to be asked wherever the exit was taken from.
  const blocker = useUnsavedChangesPrompt(hasUnsavedChanges);

  const saveDraft = useMutation({
    mutationFn: () =>
      saveCustomerOrderDraft(
        customerOrderId!,
        draftFromEdits(edits, serverValues),
      ),
    onSuccess: () => {
      // The order is read back rather than assumed: the save may have settled a value differently
      // from how it was typed, and last_updated moving is what clears the form's own record of
      // what was changed.
      void queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
      });
      void queryClient.invalidateQueries({
        queryKey: ["sales-order-awaiting-validation", workflowId],
      });
      setSaved(true);
      if (leavingAfterSave) {
        setLeavingAfterSave(false);
        blocker.proceed?.();
      }
    },
    onError: (error: Error) => {
      // The page is left where it is: the work is still unsaved, and leaving now would lose it.
      setLeavingAfterSave(false);
      toast({
        title: "Could not save the draft",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Both of these take the order out of the queue, so the lists it was in and the order itself
  // all have to be read again — the status badge, the tab it belongs to and the badge counts.
  function afterLeavingTheQueue(title: string) {
    void queryClient.invalidateQueries({
      queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
    });
    invalidateSalesOrderData(queryClient, workflowId);
    toast({ title });
  }

  const manualHandling = useMutation({
    mutationFn: () => markManualHandlingCustomerOrder(customerOrderId!),
    onSuccess: () => afterLeavingTheQueue("Moved to manual handling"),
    onError: (error: Error) =>
      toast({
        title: "Could not move to manual handling",
        description: error.message,
        variant: "destructive",
      }),
  });

  const skipOrder = useMutation({
    mutationFn: () => skipCustomerOrder(customerOrderId!),
    onSuccess: () => afterLeavingTheQueue("Order skipped"),
    onError: (error: Error) =>
      toast({
        title: "Could not skip the order",
        description: error.message,
        variant: "destructive",
      }),
  });

  const addRow = useMutation({
    mutationFn: (row: NewCustomerOrderRow) =>
      createCustomerOrderRow(customerOrderId!, row),
    onSuccess: () => {
      // The order is read back rather than the row assumed: the position may have been settled
      // for us, and the audit log below has gained an entry either way.
      void queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
      });
      toast({ title: "Article added" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not add the article",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
        variant: "destructive",
      }),
  });

  const dropRow = useMutation({
    mutationFn: (rowId: string) =>
      deleteCustomerOrderRow(customerOrderId!, rowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
      });
      toast({ title: "Row removed" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Could not remove the row",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
        variant: "destructive",
      }),
  });

  const createSalesOrder = useMutation({
    // Two calls, in order. Creating is its own endpoint and writes nothing of its own, so the
    // form is saved first and the order is created from what was saved. A save that fails stops
    // here with nothing sent, which is why it is told apart from a failed create below: the work
    // is still on screen and still unsaved.
    mutationFn: async () => {
      const draft = draftFromEdits(edits, serverValues);
      if (Object.keys(draft).length > 0) {
        try {
          await saveCustomerOrderDraft(customerOrderId!, draft);
        } catch (error) {
          throw new DraftNotSavedError(
            error instanceof Error ? error.message : "Something went wrong.",
          );
        }
      }
      return createCustomerOrderSalesOrder(customerOrderId!);
    },
    onSuccess: (result) => {
      // The order is read back for the same reason a draft save reads it back, and because this is
      // what closes the form: it is the order's own validated flag that locks it, not this call.
      void queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKeys.customerOrder(customerOrderId!),
      });
      invalidateSalesOrderData(queryClient, workflowId);
      setSaved(true);

      const problems = (result?.erp_errors ?? [])
        .map((message) => message.trim())
        .filter(Boolean);
      if (problems.length > 0) {
        // The order exists in the ERP either way. Saying so first, and then what did not go, keeps
        // anybody from creating it a second time.
        setErpErrors(problems);
        setShowErpErrors(true);
        toast({
          title: result?.order_number
            ? `Order ${result.order_number} created`
            : "Order created",
          description:
            "Not everything after that succeeded. Review the details.",
        });
        return;
      }
      // The order number is what makes the toast worth reading, but its absence is not worth
      // failing over: the order is in the ERP either way, and saying nothing would be worse.
      toast({
        title: result?.order_number
          ? `Order ${result.order_number} created and sent`
          : "Order created and sent",
      });
    },
    onError: (error: unknown) => {
      // Nothing was created, so the form stays as it is and can be corrected and tried again.
      if (error instanceof DraftNotSavedError) {
        toast({
          title: "Could not save the order, so nothing was sent",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (error instanceof ApiRequestError && error.erpErrors?.length) {
        setErpErrors(error.erpErrors);
        setShowErpErrors(true);
      }
      toast({
        title: "Could not create the order",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
        variant: "destructive",
      });
    },
  });

  // "Saved" is only ever said about a save that happened and was confirmed, so it waits on the
  // mutation rather than on the form merely matching the order.
  const saveState: SaveState = hasUnsavedChanges
    ? "unsaved"
    : saved
      ? "saved"
      : null;

  // Back goes to the tab the order is actually in. An order the ERP has raised has left the
  // validation queue, and sending someone back to a list their order is no longer on reads as
  // having lost it.
  const overviewTab = detail?.sales_order_version ? "done" : "awaiting_validation";
  const overviewPath = `/sales-order/${workflowId}/overview/${
    overviewTab === "done" ? "done" : "awaiting-validation"
  }`;

  if (isLoading) {
    return (
      <div className="flex justify-center py-16 text-sm text-gray-400">
        Loading…
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className="max-w-lg space-y-4">
        <p className="text-sm text-gray-600">
          Could not load this customer order.
        </p>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to={overviewPath}>Back to overview</Link>
        </Button>
      </div>
    );
  }

  const email = detail.source.email;
  const attachment = detail.source.attachment;
  const rows = detail.customer_order_rows;

  const showingSalesOrder = salesOrderVersion !== null && viewMode === "sales-order";

  // A raised order cannot be edited, whichever of the two documents is on screen. Validated says
  // the same thing for an order that was sent without the ERP answering with its own copy, so
  // either is enough to put the customer order beyond changing.
  const readOnly = detail.validated || salesOrderVersion !== null;

  return (
    <div className="container mx-auto space-y-4 pb-20">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1 text-on-canvas-muted hover:bg-white/40 hover:text-on-canvas"
          asChild
        >
          <Link to={overviewPath}>
            <ArrowLeft className="h-4 w-4" />
            {SALES_ORDER_METRIC_COPY[overviewTab].label}
          </Link>
        </Button>

        <div className="flex min-w-0 flex-1 justify-center">
          {/* The chain of documents replaces the row statistics once the order is raised: the
              statistics are about deciding whether to send it, and that decision has been made. */}
          {salesOrderVersion ? (
            <DocumentNav mode={viewMode} onSelect={setViewMode} />
          ) : (
            <RowStats
              rows={rows}
              articles={articles}
              stock={readings.stock}
              demand={demand}
              checkStock={checkStock}
            />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {detail.sales_order?.order_number && (
            <Badge tone="success">
              Sales order {detail.sales_order.order_number}
            </Badge>
          )}
          <CustomerOrderStatusBadge status={detail.internal_status} />
        </div>
      </div>

      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {/* LEFT — the order, then its rows. Which order depends on whether the ERP has raised
            one: once it has, its copy is the record and ours is the request behind it. */}
        <div className="min-w-0 space-y-4">
          {showingSalesOrder && salesOrderVersion?.sales_order_head ? (
            <>
              <SalesOrderHeadPanel head={salesOrderVersion.sales_order_head} />
              <SalesOrderRowsPanel rows={salesOrderVersion.sales_order_rows} />
            </>
          ) : (
            <>
              <OrderHeadPanel
                detail={detail}
                onFieldChange={changeField}
                readOnly={readOnly}
              />
              <OrderRowsPanel
                readOnly={readOnly}
                detail={detail}
                articles={articles}
                onPickArticle={(rowId, picked) => {
                  setArticles((current) => ({ ...current, [rowId]: picked }));
                  changeField(rowField(rowId, "article_number"), picked.number);
                }}
                onFieldChange={changeField}
                demand={demand}
                readings={readings}
                onDeleteRow={setRowToDrop}
              />
            </>
          )}
          {/*
            Under the lines rather than above them. Adding one by hand is the rare answer to a
            document we read badly, not part of working through the rows, and a button over the
            table reads as the way rows are meant to arrive.

            An order already in the ERP cannot gain a line, so there is nothing to offer on one.
          */}
          {!readOnly && !showingSalesOrder && (
            <div className="flex justify-start">
              <AddRowPopover
                workflowId={workflowId!}
                onAdd={(row) => addRow.mutate(row)}
                adding={addRow.isPending}
              />
            </div>
          )}
        </div>

        {/*
          RIGHT — the document, then the mail it arrived in, as the order confirmation page has
          it. The order is the attachment; the mail around it is context for the rows, and reads
          as such underneath rather than as the thing being validated.
        */}
        <Container className="min-w-0 overflow-hidden">
          <div className="space-y-4 p-4">
            {/* Source, not Attachment: the document and the mail it came in are one answer to
                where this order came from, and the label heads both. */}
            {attachment && (
              <AttachmentSection attachment={attachment} label="Source" />
            )}
            {/* No label on the email: the column is named, and the block opens with the subject
                and the sender, which says what it is more plainly than a heading would. */}
            {email && (
              <EmailSourceSection
                email={email}
                attachment={attachment}
                resetKey={detail.id}
              />
            )}
            {/* Under the document and the mail it came in, where the order confirmation page
                keeps its own: the record of what has been done to the order, after the record of
                where the order came from. */}
            {(detail.changes?.length ?? 0) > 0 && (
              <>
                <div className="border-t border-gray-100" />
                <CustomerOrderAuditLog changes={detail.changes ?? []} />
              </>
            )}
            {/* Ours, not the customer's: ExtractionLink draws nothing unless the
                signed-in organization is FrontFlow, and the endpoint refuses
                anyone else regardless. Last in the column, under where the order
                came from and what has been done to it, because it answers a
                question only asked when one of those looks wrong. */}
            <div className="border-t border-gray-100 pt-3">
              <ExtractionLink customerOrderId={detail.id} />
            </div>
          </div>
        </Container>
      </div>

      {/* Only an order still awaiting validation can be taken out of the queue: the backend
          refuses the rest, and a menu item that always errors is worse than one that is grey. */}
      <ActionBar
        saveState={saveState}
        onSaveDraft={() => saveDraft.mutate()}
        canSaveDraft={hasUnsavedChanges}
        saving={saveDraft.isPending}
        onManualHandling={() => manualHandling.mutate()}
        onSkip={() => setShowSkipConfirm(true)}
        onCreateOrder={() => createSalesOrder.mutate()}
        creating={createSalesOrder.isPending}
        actionsEnabled={
          detail.internal_status === "open" &&
          !manualHandling.isPending &&
          !detail.validated
        }
        // An order the pipeline raised is in the ERP without anyone having validated it here, so
        // "already sent" is the sales order's existence rather than our own validated flag.
        sent={readOnly}
      />

      {/* What the ERP said, in its own words. It appears both when the order went and something
          after it did not, and when nothing went at all; the title is what tells them apart, since
          the difference decides whether trying again would create a second order. */}
      <Dialog.Root open={showErpErrors} onOpenChange={setShowErpErrors}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[min(85vh,32rem)] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              {detail.validated
                ? "Created with warnings"
                : "The order was not created"}
            </Dialog.Title>
            <Dialog.Description className="mt-2 shrink-0 text-sm text-gray-500">
              {detail.validated
                ? "The order is in your ERP, but not everything after that succeeded."
                : "Nothing was created. Correct what is below and try again."}
            </Dialog.Description>
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 text-sm text-gray-800">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                What your ERP reported
              </p>
              <ul className="space-y-2">
                {erpErrors.map((message, index) => (
                  <li
                    key={`erp-${index}-${message}`}
                    className="rounded-md border border-amber-200/80 bg-amber-50/60 px-3 py-2 leading-snug"
                  >
                    {message}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-5 flex shrink-0 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowErpErrors(false)}
              >
                Close
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/*
        Dropping a line is asked about rather than offered back: the row is gone from the order
        from here on, and the log is the only place it is still readable.
      */}
      <Dialog.Root
        open={rowToDrop !== null}
        onOpenChange={(open) => !open && setRowToDrop(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Remove row
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              {rowToDrop
                ? `${rowToDrop.article_number || "This article"} will be removed from the order and will not be ordered. This cannot be undone.`
                : ""}
            </Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRowToDrop(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={dropRow.isPending}
                onClick={() => {
                  if (rowToDrop) dropRow.mutate(rowToDrop.id);
                  setRowToDrop(null);
                }}
              >
                Remove row
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Skip Order
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              Skipping a customer order marks it as canceled. This is for an
              order that should not be placed. I.e. a duplicate, or one the
              customer has withdrawn. No sales order will be created for it.
            </Dialog.Description>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSkipConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={skipOrder.isPending}
                onClick={() => {
                  setShowSkipConfirm(false);
                  skipOrder.mutate();
                }}
              >
                {skipOrder.isPending ? "Skipping…" : "Skip Order"}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <UnsavedChangesDialog
        open={blocker.state === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => blocker.proceed?.()}
        onSave={() => {
          setLeavingAfterSave(true);
          saveDraft.mutate();
        }}
        saving={saveDraft.isPending}
      />
    </div>
  );
}
